import { Shipment } from '@zooplus/checkout-state';
import { DeploymentEnv } from '@zooplus/commons-lang';
import { PLATFORM_TYPE, TRACKING_PAGE, TrackingPage } from '@zooplus/tracker-listener';

import {
  ENVS,
  PAGE_PATH,
  EMAIL_REGEX,
  breakpointsNumeric,
  KEYCLOAK_DEV_BASE_URL,
  KEYCLOAK_PROD_BASE_URL,
  CART_MESSAGE_ACTION_TYPE,
  EXTERNAL_SCRIPT_LOADER_SCRIPT_PROD,
  EXTERNAL_SCRIPT_LOADER_SCRIPT_DEV,
  CART_TYPE,
  SHIPMENT_TYPE,
} from './constants';

//validate if array data present
export const isArrayNotEmpty = <T>(array?: T[] | null): array is T[] => {
  return Array.isArray(array) && array.length > 0;
};

//validate object
export const isObjectNotNullOrEmpty = (objectVal: null | undefined | object) => {
  return !!objectVal && typeof objectVal === 'object' && Object.keys(objectVal).length !== 0;
};

export const isMobileViewport = (breakpointValue?: keyof typeof breakpointsNumeric) =>
  window.innerWidth < breakpointsNumeric[breakpointValue || 'breakpointLg'];

export const handleKeyboardKeyDown = (
  event: React.KeyboardEvent,
  callbackMethod?: Function,
  extraParam?: string | undefined,
) => {
  if (event.key === 'Enter') {
    extraParam ? callbackMethod?.(extraParam) : callbackMethod?.();
  }
};

export const splitShopId = (shopId: string) => {
  return shopId.split('.');
};

export const getEnvType = (environment: DeploymentEnv): DeploymentEnv => {
  return [ENVS.PROD, ENVS.BETA].includes(environment) ? ENVS.PROD : ENVS.DEV;
};

export const getPageType = (): TrackingPage => {
  const path = window.location.pathname;
  if (path.includes('/cart')) {
    return TRACKING_PAGE.CHECKOUT_CART;
  }
  if (path.includes('/preview')) {
    return TRACKING_PAGE.CHECKOUT_PREVIEW;
  }
  if (path.includes('/guest-preview')) {
    return TRACKING_PAGE.CHECKOUT_PREVIEW_GUEST;
  }
  if (path.includes('/finish')) {
    return TRACKING_PAGE.CHECKOUT_FINISH;
  }
  return 'CHECKOUT';
};

export const getSearchParams = (url: string | URL) => {
  const currentUrl = new URL(url);
  return new URLSearchParams(currentUrl.search);
};

export const checkWindowMedia = (mediaType: string, resolution: string): boolean => {
  return window.matchMedia(`(${mediaType}: ${resolution})`).matches;
};

export function applyStyleBasedOnResolution(
  elementSelector: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  styleProperty: any,
  mediaType: string,
  mobileResolution: string,
  values: { mobile: string; desktop: string },
) {
  const element = document.querySelector(elementSelector) as HTMLElement;
  const isMobileResolution = checkWindowMedia(mediaType, mobileResolution);
  let value = '';

  if (isMobileResolution) {
    value = values.mobile;
  } else {
    value = values.desktop;
  }

  if (element) {
    element.style[styleProperty] = value;
  }
}

export const redirectToUrl = (url: string) => (window.location.href = url);
export const reloadPage = () => window.location.reload();

export const goToCart = (isLoading: boolean, isNewOverviewPage: boolean, hasArticles: boolean) => {
  if (!isLoading && !isNewOverviewPage && hasArticles) {
    redirectToUrl(PAGE_PATH.OVERVIEW);
  }
};

export const goToPreview = (isLoading: boolean, hasArticles: boolean, isGuestCustomer: boolean) => {
  if (isLoading || !hasArticles) {
    return;
  }

  const url = isGuestCustomer ? PAGE_PATH.GUEST_PREVIEW : PAGE_PATH.PREVIEW;
  redirectToUrl(url);
};

export const getCartMessageActionData = (
  t: (arg: string) => string,
  actionType?: string | undefined,
  handleCouponAddition?: Function,
  handleCouponRemoval?: Function,
  handleRestoreArticle?: Function,
) => {
  switch (actionType) {
    case CART_MESSAGE_ACTION_TYPE.ADD_COUPON:
      return {
        handleOnClick: handleCouponAddition,
        label: t('cart.feedback.coupon.redeem'),
        cartMessageActionTestId: 'redeem-coupon-from-message',
      };

    case CART_MESSAGE_ACTION_TYPE.REMOVE_COUPON:
      return {
        handleOnClick: handleCouponRemoval,
        label: t('loy.coupons.component.remove.coupon'),
        cartMessageActionTestId: 'remove-coupon-btn',
      };

    default:
      return {
        handleOnClick: handleRestoreArticle,
        label: t('cart.undo.remove.item.button'),
        cartMessageActionTestId: 're-add-article-alert',
      };
  }
};

export const getKeycloakUrl = (environment: DeploymentEnv) => {
  return getEnvType(environment) === ENVS.PROD ? KEYCLOAK_PROD_BASE_URL : KEYCLOAK_DEV_BASE_URL;
};

export const getMktExternalUrl = (environment: DeploymentEnv) => {
  return getEnvType(environment) === ENVS.PROD
    ? EXTERNAL_SCRIPT_LOADER_SCRIPT_PROD
    : EXTERNAL_SCRIPT_LOADER_SCRIPT_DEV;
};

export const validateEmail = (email: string) => EMAIL_REGEX.test(String(email).toLowerCase());

export const setAttributeToElement = (
  selector: string,
  attributeValue: string,
  attributeName: string,
  isClass: boolean = false,
) => {
  let elements;
  if (isClass) {
    elements = document.getElementsByClassName(selector);
  } else {
    elements = [document.getElementById(selector)];
  }

  if (elements && attributeValue) {
    Array.from(elements).forEach((element) => {
      if (element) {
        (element as HTMLElement).setAttribute(attributeName, attributeValue);
      }
    });
  }
};

export const addScript = (scriptSource: string, key: string, async: boolean, defer: boolean) => {
  const script = document.createElement('script');
  script.src = scriptSource;
  script.setAttribute('data-for', key);
  script.defer = defer;
  script.async = async;
  document.head.appendChild(script);
};

export const trimLeadingHyphenAndSpaces = (string: string | null | undefined): string =>
  (string ?? '').replace(/^-\s*/, '').trim();

export const getSellerType = (shipmentId: number | undefined) => {
  if (shipmentId && shipmentId !== 1) {
    return SHIPMENT_TYPE.THIRD_PARTY;
  } else {
    return SHIPMENT_TYPE.FIRST_PARTY;
  }
};

export const configureLinkElements = (
  links: HTMLAnchorElement[],
  handler: (e: Event) => void,
  url?: string,
) => {
  links.forEach((link) => {
    link.addEventListener('click', handler);
    link.setAttribute('href', url ?? '');
  });

  return () => {
    links.forEach((link) => {
      link.removeEventListener('click', handler);
    });
  };
};

export const getDeviceType = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;

  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return PLATFORM_TYPE.IOS;
  }

  if (/android/i.test(userAgent)) {
    return PLATFORM_TYPE.ANDROID;
  }

  return PLATFORM_TYPE.WEB;
};

export const transformType = (type: string) => {
  return type.replaceAll(/([a-z])([A-Z])/g, '$1_$2').toUpperCase();
};

export const getCartType = (shipments: Shipment[] | undefined) => {
  if (!Array.isArray(shipments) || shipments.length === 0) {
    return null;
  }

  let hasFirstParty = false;
  let hasThirdParty = false;

  for (const shipment of shipments) {
    const type = shipment.seller?.sellerType;

    switch (type) {
      case SHIPMENT_TYPE.FIRST_PARTY:
        hasFirstParty = true;
        break;
      case SHIPMENT_TYPE.THIRD_PARTY:
        hasThirdParty = true;
        break;
      default:
        break;
    }

    if (hasFirstParty && hasThirdParty) {
      return CART_TYPE.MIXED;
    }
  }

  if (hasFirstParty) {
    return CART_TYPE.FIRST_PARTY;
  }
  if (hasThirdParty) {
    return CART_TYPE.THIRD_PARTY;
  }
  return null;
};

export const getFeatureExperimentValue = (
  togglesAndExperiments: Array<{ id: string; value: string }> | undefined,
  experimentId: string,
) => {
  return togglesAndExperiments?.find(
    (experiment: { id: string; value: string }) => experiment.id === experimentId,
  )?.value;
};

export const getV2OfferIds = (
  subscriptionAppliedValue: boolean,
  abdApplied = false,
  abdOfferId?: string,
  offerId?: string,
  ausOfferId?: string,
) => {
  return [
    subscriptionAppliedValue ? ausOfferId : !abdApplied ? offerId : undefined,
    abdApplied ? abdOfferId : undefined,
  ].filter((offer): offer is string => Boolean(offer));
};
